import React, { useState } from 'react';
import {
  ShieldCheck, Info, Mail, PlayCircle, ArrowRight,
} from 'lucide-react';
import { MarketingLayout, goToAuth } from '../marketing/MarketingLayout';
import { PAGE_META } from '../marketing/siteConfig';
import { MethodologyWeights } from './research/MethodologyWeights';
import { useSeo } from '../marketing/seo';
import { PlanCards } from '../marketing/sections/PlanCards';
import { CANCEL_ANYTIME } from '../marketing/plans';
import './landing-new.css';

const LAST_UPDATED = 'Last updated: June 2026';

const PublicLayout = ({ path, children }) => {
  const meta = PAGE_META[path];
  useSeo({ title: `${meta.title} | TradeHive AI`, description: meta.description, path });
  return (
    <MarketingLayout onAuth={goToAuth}>
      <div className="th-public-body">
        <div className="mk-container">
          <div className="th-doc mk-rise">{children}</div>
        </div>
      </div>
    </MarketingLayout>
  );
};

const Disclaimer = () => (
  <div className="th-callout">
    <Info size={18} />
    <p>TradeHive AI is for research and educational purposes only. It does not provide investment, legal, or tax advice. Market data and AI-generated outputs can be inaccurate, delayed, or incomplete.</p>
  </div>
);

/* ---------------- Individual pages ---------------- */

const Methodology = () => (
  <>
    <span className="th-eyebrow">Methodology</span>
    <h1>How our AI-assisted signals work</h1>
    <p className="th-lead">Transparency matters when you rely on data to research the market. This page explains, in plain language, how TradeHive AI produces the signals and context you see in the workspace.</p>
    <Disclaimer />
    <MethodologyWeights />
    <h2>What we combine</h2>
    <p>Our research workspace brings together several inputs: market data (intraday and historical), options activity, and commonly used technical indicators. Market data may be delayed depending on your plan and market conditions. AI-assisted analysis is layered on top to summarize context and surface areas that may be worth a closer look.</p>
    <h2>How signals are generated</h2>
    <ul>
      <li>Market and options data is collected and normalized for the tickers you track.</li>
      <li>Technical indicators (such as trend, momentum, and volatility measures) are calculated from that data.</li>
      <li>AI-assisted analysis interprets the combined context and presents it alongside the underlying figures.</li>
      <li>Each signal is shown with supporting context so you can evaluate it yourself.</li>
    </ul>
    <h2 id="outlook-score">How an AI Search outlook is scored</h2>
    <ul>
      <li><strong>Indicators in code:</strong> RSI (14), EMA 21/50/200, MACD, relative volume (5-day vs 20-day) and ATR (14) are computed deterministically from daily bars.</li>
      <li><strong>Score (0 to 100):</strong> a weighted blend of momentum, trend, volume and volatility components. The mean-reversion strategy adds a 20-day z-score input; the options-flow strategy adds the call share of options premium. A score of 60 or more reads Bullish, 40 or less Bearish, otherwise Neutral.</li>
      <li><strong>Probability:</strong> the chance of closing above the current price at the end of the horizon, estimated from 60-day drift and 20-day volatility of daily returns. Each probability is shown with this definition.</li>
      <li><strong>Levels:</strong> target = entry ± 1.5 × ATR × √(horizon days); stop = entry ∓ 1.5 × ATR. The risk level (Low, Medium, High) comes from ATR as a percentage of price.</li>
      <li><strong>Explanation:</strong> the AI writes the thesis from these numbers. If its text uses numbers that are not in the data, it is regenerated or replaced with a template summary.</li>
    </ul>
    <h2 id="track-record">How outlooks are graded</h2>
    <p>Every outlook is saved with a timestamp and its inputs. When its timeframe ends, it is graded against the daily closing price: a hit means price moved in the predicted direction. Neutral outlooks are not counted. The public track record summary is shown only once at least 30 outlooks have been graded. Historical results do not guarantee future performance.</p>
    <h2 id="daily-briefing">How the Daily Market Snapshot briefing is created</h2>
    <p>The "day in 60 seconds" briefing is written by an AI model using only a fixed set of facts our servers collect that morning: ETF stand-in prices for the major indices, Treasury yields, the latest official macro readings, market-calendar events and recent headlines. Every number in the briefing is checked against those facts in code. If the text uses a number that is not in the data, or uses advice or price-target language, it is regenerated once; if it fails again, a plain template summary built directly from the data is shown instead. Each bullet lists its sources, and every briefing is logged with its inputs for quality review. The briefing describes events and context only. It never gives trade instructions.</p>
    <h2>What signals are — and are not</h2>
    <p>Signals are research prompts, not recommendations. They are intended to help you decide where to focus your own analysis. They are not predictions of future performance and should never be treated as advice to buy or sell any security.</p>
    <h2>Limitations</h2>
    <p>AI-generated outputs and market data can be inaccurate, delayed, or incomplete. Models can misinterpret context, and past behavior does not indicate future results. Always independently verify information before acting on it.</p>
    <p className="th-doc-meta">{LAST_UPDATED}</p>
  </>
);

const DataSources = () => (
  <>
    <span className="th-eyebrow">Data Sources</span>
    <h1>Where our market data comes from</h1>
    <p className="th-lead">TradeHive AI relies on market data feeds to power quotes, options activity, and technical context. This page explains how data flows through the platform.</p>
    <Disclaimer />
    <h2>Types of data</h2>
    <ul>
      <li>Equity and index price data, including intraday and historical quotes.</li>
      <li>Options market data, including contract-level activity and flow.</li>
      <li>Derived technical indicators calculated from the above.</li>
    </ul>
    <h2>Timeliness and accuracy</h2>
    <p>Depending on your plan and market conditions, some data may be delayed. Data feeds can also contain gaps or errors outside of our control. We surface data as reliably as we can, but we cannot guarantee that it is complete, accurate, or real-time at all times.</p>
    <h2>Your responsibility</h2>
    <p>Because data can be delayed or incorrect, you should independently verify any figure before relying on it for a decision. TradeHive AI is a research tool, not a system of record for trading or accounting.</p>
    <h2>Providers we use</h2>
    <ul>
      <li><strong>Polygon.io</strong>: stock, ETF and options prices, options trades, market status and news.</li>
      <li><strong>Federal Reserve, BLS and U.S. Treasury data (delivered via Polygon.io)</strong>: Treasury yields, inflation and labor-market readings used in the Daily Market Snapshot.</li>
      <li><strong>OpenAI GPT-4o</strong>: writes the plain-language explanations. It does not supply market data; numbers it uses are checked against the data above.</li>
    </ul>
    <p>The ticker tape on our public pages shows quotes delayed by at least 15 minutes, with the time of the last quote. If the feed is unavailable, the tape is hidden rather than showing stale numbers.</p>
    <p className="th-doc-meta">{LAST_UPDATED}</p>
  </>
);

const Security = () => (
  <>
    <span className="th-eyebrow">Security</span>
    <h1>How we approach security</h1>
    <p className="th-lead">Protecting your account and data is a priority. This page describes our general approach to security and how to report a concern.</p>
    <h2>Account protection</h2>
    <ul>
      <li>Passwords are stored using industry-standard one-way hashing.</li>
      <li>Access to your account requires authentication, and sessions can be invalidated when needed.</li>
      <li>Data is transmitted over encrypted connections (HTTPS).</li>
    </ul>
    <h2>Payments</h2>
    <p>Subscription payments are processed by a third-party payment provider. TradeHive AI does not store full card numbers on its own servers.</p>
    <h2>Reporting a vulnerability</h2>
    <p>If you believe you have found a security issue, please contact us through our <a className="inline" href="/contact">support page</a> so we can investigate. We appreciate responsible disclosure.</p>
    <div className="th-callout"><ShieldCheck size={18} /><p>We describe only the practices we actually follow. Specific certifications or third-party audit details will be published here only once they are in place and verifiable.</p></div>
    <p className="th-doc-meta">{LAST_UPDATED}</p>
  </>
);

const Privacy = () => (
  <>
    <span className="th-eyebrow">Privacy Policy</span>
    <h1>Privacy Policy</h1>
    <p className="th-lead">This policy explains what information TradeHive AI collects and how it is used. It is provided for transparency and will be updated as the product evolves.</p>
    <h2>Information we collect</h2>
    <ul>
      <li>Account information you provide, such as your name and email address.</li>
      <li>Usage information about how you interact with the platform.</li>
      <li>Preferences such as watchlists and alert settings.</li>
    </ul>
    <h2>How we use information</h2>
    <p>We use your information to provide and improve the service, to authenticate your account, to communicate with you, and to process subscriptions. We do not sell your personal information.</p>
    <h2>Third-party services</h2>
    <p>We rely on service providers for functions such as payment processing and market data. These providers process data only as needed to deliver their service.</p>
    <h2>Your choices</h2>
    <p>You can update your account details or request deletion of your account by contacting <a className="inline" href="/contact">support</a>.</p>
    <div className="th-callout"><Info size={18} /><p>This is a plain-language summary intended as a starting point. A final legal policy should be reviewed by qualified counsel before launch.</p></div>
    <p className="th-doc-meta">{LAST_UPDATED}</p>
  </>
);

const Terms = () => (
  <>
    <span className="th-eyebrow">Terms of Service</span>
    <h1>Terms of Service</h1>
    <p className="th-lead">By using TradeHive AI, you agree to these terms. Please read them carefully.</p>
    <Disclaimer />
    <h2>Use of the service</h2>
    <p>TradeHive AI provides an AI-assisted market research workspace. You agree to use it lawfully and not to misuse or attempt to disrupt the service.</p>
    <h2>No investment advice</h2>
    <p>Nothing provided through TradeHive AI constitutes investment, legal, or tax advice. All decisions you make are your own responsibility. You should independently evaluate all information and consider consulting a qualified professional.</p>
    <h2>Subscriptions</h2>
    <p>Paid features are offered on a subscription basis. You can cancel at any time; details of billing and trials are shown on the <a className="inline" href="/pricing">Pricing</a> page.</p>
    <h2>Limitation of liability</h2>
    <p>The service and its data are provided “as is.” To the maximum extent permitted by law, TradeHive AI is not liable for losses arising from your use of the platform or reliance on its data or outputs.</p>
    <div className="th-callout"><Info size={18} /><p>This is a plain-language summary. A final agreement should be reviewed by qualified counsel before launch.</p></div>
    <p className="th-doc-meta">{LAST_UPDATED}</p>
  </>
);

const RiskDisclosure = () => (
  <>
    <span className="th-eyebrow">Risk Disclosure</span>
    <h1>Risk Disclosure</h1>
    <p className="th-lead">Trading stocks and options involves significant risk. Please understand these risks before using market research tools to inform any decision.</p>
    <Disclaimer />
    <h2>Market risk</h2>
    <p>The value of securities can rise and fall. You may lose some or all of your invested capital. Options in particular can expire worthless and carry additional risks, including leverage and time decay.</p>
    <h2>Not a recommendation</h2>
    <p>TradeHive AI does not recommend any security, strategy, or transaction. Signals and analysis are research context only, and are not tailored to your personal circumstances.</p>
    <h2>Data and model limitations</h2>
    <p>Market data and AI-generated outputs can be inaccurate, delayed, or incomplete. Do not rely on any single figure or signal without independent verification.</p>
    <h2>Your responsibility</h2>
    <p>You are solely responsible for your own decisions. Consider your objectives, experience, and risk tolerance, and seek advice from a licensed professional where appropriate.</p>
    <p className="th-doc-meta">{LAST_UPDATED}</p>
  </>
);

const Documentation = () => (
  <>
    <span className="th-eyebrow">Documentation</span>
    <h1>Documentation</h1>
    <p className="th-lead">Guides to help you get the most out of the TradeHive AI research workspace.</p>
    <h2>Getting started</h2>
    <ul>
      <li>Create your account and start your free trial.</li>
      <li>Add tickers to a watchlist to personalize your workspace.</li>
      <li>Review AI-assisted signals alongside technical context.</li>
      <li>Set configurable alerts for the conditions you care about.</li>
    </ul>
    <h2>Core areas</h2>
    <ul>
      <li><strong>AI Market Signals</strong> — context-aware signals with supporting reasoning.</li>
      <li><strong>Options Flow &amp; Market Data</strong> — live data with card and list views.</li>
      <li><strong>Charts &amp; Technical Context</strong> — indicators and price context.</li>
      <li><strong>Alerts, Watchlists &amp; Portfolio</strong> — organization and notifications.</li>
    </ul>
    <div className="th-callout"><Info size={18} /><p>Detailed step-by-step guides and screenshots are being expanded. For help in the meantime, visit the <a className="inline" href="/help">Help Center</a> or <a className="inline" href="/contact">contact support</a>.</p></div>
    <p className="th-doc-meta">{LAST_UPDATED}</p>
  </>
);

const HelpCenter = () => (
  <>
    <span className="th-eyebrow">Help Center</span>
    <h1>Help Center</h1>
    <p className="th-lead">Answers to common questions and ways to reach us.</p>
    <h2>Common topics</h2>
    <ul>
      <li>Account &amp; sign-in — how to create, access, or reset your account.</li>
      <li>Subscriptions &amp; billing — trials, plans, and cancellation.</li>
      <li>Using the workspace — signals, data, alerts, and watchlists.</li>
      <li>Data questions — timeliness, accuracy, and limitations.</li>
    </ul>
    <h2>Still need help?</h2>
    <p>Reach our team through the <a className="inline" href="/contact">contact page</a> and we’ll get back to you. You can also review the <a className="inline" href="/documentation">documentation</a> for guidance.</p>
    <p className="th-doc-meta">{LAST_UPDATED}</p>
  </>
);

const BookDemo = () => {
  const [form, setForm] = useState({ name: '', email: '', note: '' });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Please enter your name.';
    if (!form.email.trim()) e.email = 'Please enter your email.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Please enter a valid email address.';
    return e;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length) return;
    setStatus('submitting');
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/demo-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error('request failed');
      setStatus('success');
    } catch {
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <div className="th-form-success" data-testid="demo-success">
        Thanks — your demo request has been received. We’ll reach out by email soon.
      </div>
    );
  }

  return (
    <form className="th-form" onSubmit={submit} noValidate data-testid="demo-form">
      <div className="th-field">
        <label htmlFor="d-name">Name</label>
        <input id="d-name" type="text" value={form.name}
          aria-invalid={!!errors.name} aria-describedby={errors.name ? 'd-name-err' : undefined}
          onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="demo-name" />
        {errors.name && <div className="th-field-err" id="d-name-err">{errors.name}</div>}
      </div>
      <div className="th-field">
        <label htmlFor="d-email">Work email</label>
        <input id="d-email" type="email" value={form.email}
          aria-invalid={!!errors.email} aria-describedby={errors.email ? 'd-email-err' : undefined}
          onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="demo-email" />
        {errors.email && <div className="th-field-err" id="d-email-err">{errors.email}</div>}
      </div>
      <div className="th-field">
        <label htmlFor="d-note">What would you like to see? <span style={{ color: 'var(--th-text-dim)', fontWeight: 400 }}>(optional)</span></label>
        <textarea id="d-note" rows={3} value={form.note}
          onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="demo-note" />
      </div>
      {status === 'error' && (
        <div className="th-field-err" data-testid="demo-error">Something went wrong. Please try again or email us via the contact page.</div>
      )}
      <button className="mk-btn mk-btn-primary mk-btn-lg" type="submit" disabled={status === 'submitting'} data-testid="demo-submit">
        {status === 'submitting' ? 'Sending…' : 'Book a demo'}
      </button>
    </form>
  );
};

const ProductDemo = () => (
  <>
    <span className="th-eyebrow">Product Demo</span>
    <h1>See TradeHive AI in action</h1>
    <p className="th-lead">A guided look at the research workspace — signals, market data, charts, and alerts working together in one place.</p>
    <div className="th-callout"><PlayCircle size={18} /><p>A full walkthrough video is on the way. In the meantime, start a free trial to explore the live workspace, or book a demo below and we’ll walk you through it.</p></div>
    <h2>What you’ll explore</h2>
    <ul>
      <li>AI-assisted market signals with the reasoning behind them.</li>
      <li>Live options flow in card and list views.</li>
      <li>Charts with technical indicators and price context.</li>
      <li>Configurable alerts, watchlists, and portfolio organization.</li>
    </ul>
    <h2>Book a demo</h2>
    <p>Leave your details and we’ll reach out to schedule a walkthrough. For research and educational purposes only — not investment advice.</p>
    <BookDemo />
    <div className="th-final-ctas" style={{ justifyContent: 'flex-start', marginTop: '2rem' }}>
      <a className="mk-btn mk-btn-ghost mk-btn-lg" href="/?auth=signup" data-testid="demo-trial-cta">Or start a free trial <ArrowRight size={18} /></a>
    </div>
    <p className="th-doc-meta">For research and educational purposes only. Not investment advice.</p>
  </>
);

const Pricing = () => (
  <>
    <span className="th-eyebrow">Pricing</span>
    <h1>Simple pricing for serious research</h1>
    <p className="th-lead">Start with a free trial, then continue on TradeHive Pro, billed monthly or yearly. Prices below come from the same plan configuration used at checkout.</p>
    <PlanCards onAuth={goToAuth} />
    {CANCEL_ANYTIME && <p className="mk-fine" style={{ marginTop: '1rem' }}>Cancel anytime. Cancel during the trial and you are not charged.</p>}
    <div className="th-callout" style={{ marginTop: '2.5rem' }}><Info size={18} /><p>Current plan prices and billing details are shown at checkout after you start your trial. TradeHive AI is a research tool and does not provide investment advice.</p></div>
    <p className="th-doc-meta">{LAST_UPDATED}</p>
  </>
);

const Contact = () => {
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [errors, setErrors] = useState({});
  const [sent, setSent] = useState(false);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = 'Please enter your name.';
    if (!form.email.trim()) e.email = 'Please enter your email.';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Please enter a valid email address.';
    if (!form.message.trim()) e.message = 'Please enter a message.';
    return e;
  };

  const submit = (ev) => {
    ev.preventDefault();
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length === 0) setSent(true);
  };

  return (
    <>
      <span className="th-eyebrow"><Mail size={14} /> Contact Support</span>
      <h1>Get in touch</h1>
      <p className="th-lead">Questions about the platform, your account, or your subscription? Send us a message and our team will respond.</p>
      {sent ? (
        <div className="th-form-success" data-testid="contact-success">
          Thanks — your message has been received. We’ll get back to you by email as soon as we can.
        </div>
      ) : (
        <form className="th-form" onSubmit={submit} noValidate data-testid="contact-form">
          <div className="th-field">
            <label htmlFor="c-name">Name</label>
            <input id="c-name" type="text" value={form.name}
              aria-invalid={!!errors.name} aria-describedby={errors.name ? 'c-name-err' : undefined}
              onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-name" />
            {errors.name && <div className="th-field-err" id="c-name-err">{errors.name}</div>}
          </div>
          <div className="th-field">
            <label htmlFor="c-email">Email</label>
            <input id="c-email" type="email" value={form.email}
              aria-invalid={!!errors.email} aria-describedby={errors.email ? 'c-email-err' : undefined}
              onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-email" />
            {errors.email && <div className="th-field-err" id="c-email-err">{errors.email}</div>}
          </div>
          <div className="th-field">
            <label htmlFor="c-msg">Message</label>
            <textarea id="c-msg" rows={5} value={form.message}
              aria-invalid={!!errors.message} aria-describedby={errors.message ? 'c-msg-err' : undefined}
              onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="contact-message" />
            {errors.message && <div className="th-field-err" id="c-msg-err">{errors.message}</div>}
          </div>
          <button className="mk-btn mk-btn-primary mk-btn-lg" type="submit" data-testid="contact-submit">Send message</button>
        </form>
      )}
      <p className="th-doc-meta">TradeHive AI is a product of SAG LLC. For research and educational purposes only — not investment advice.</p>
    </>
  );
};

const PAGES = {
  '/methodology': { title: 'Methodology', node: <Methodology /> },
  '/data-sources': { title: 'Data Sources', node: <DataSources /> },
  '/security': { title: 'Security', node: <Security /> },
  '/privacy': { title: 'Privacy Policy', node: <Privacy /> },
  '/terms': { title: 'Terms of Service', node: <Terms /> },
  '/risk-disclosure': { title: 'Risk Disclosure', node: <RiskDisclosure /> },
  '/documentation': { title: 'Documentation', node: <Documentation /> },
  '/help': { title: 'Help Center', node: <HelpCenter /> },
  '/demo': { title: 'Product Demo', node: <ProductDemo /> },
  '/pricing': { title: 'Pricing', node: <Pricing /> },
  '/contact': { title: 'Contact Support', node: <Contact /> },
};

export const PUBLIC_ROUTES = Object.keys(PAGES);

const PublicPage = ({ path }) => {
  const page = PAGES[path];
  if (!page) return null;
  return <PublicLayout path={path}>{page.node}</PublicLayout>;
};

export default PublicPage;
